import React, { useEffect, useState } from 'react';
import { FileSignature, CalendarRange, Check, X, Loader2, History } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { inputCls } from './shared';

const fmt = (d) => (d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : null);
const errMsg = (e) => e?.response?.data?.detail || 'Erreur';
const period = (c) => {
  if (c.start_date && c.end_date) return `du ${fmt(c.start_date)} au ${fmt(c.end_date)}`;
  if (c.start_date) return `à partir du ${fmt(c.start_date)}`;
  if (c.end_date) return `jusqu'au ${fmt(c.end_date)}`;
  return null;
};

export const ContractSummary = ({ p }) => {
  const per = period(p);
  const expired = p.end_date && p.end_date < new Date().toISOString().slice(0, 10);
  const history = p.contract_history || [];
  return (
    <div className="mt-2 space-y-1" data-testid={`partnership-contract-${p.id}`}>
      {per && (
        <p className={`text-xs flex items-center gap-1 ${expired ? 'text-rose-600 font-semibold' : 'text-gray-600'}`}>
          <CalendarRange className="w-3 h-3" /> Contrat {per}{expired ? ' · échu' : ''}
        </p>
      )}
      {p.terms && <p className="text-xs text-gray-700 bg-[#F5EFE1]/70 rounded-lg px-2.5 py-1.5 whitespace-pre-line">{p.terms}</p>}
      {history.length > 0 && (
        <details className="text-[11px] text-gray-500">
          <summary className="cursor-pointer flex items-center gap-1" data-testid={`partnership-history-${p.id}`}>
            <History className="w-3 h-3" /> Version {p.contract_version} · {history.length} modification(s)
          </summary>
          <ul className="mt-1 space-y-0.5 pl-4 list-disc">
            {[...history].reverse().map((h) => (
              <li key={h.version}>
                v{h.version} le {fmt(h.applied_at)} : {h.previous?.commission_split}% → {h.changes.commission_split}%
                {h.accepted_by_name ? ` · acceptée par ${h.accepted_by_name}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

const Diff = ({ label, before, after }) => (before ?? '') === (after ?? '') ? null : (
  <p className="text-xs"><span className="font-semibold">{label} :</span> <span className="line-through text-gray-400">{before || '—'}</span> → <b>{after || '—'}</b></p>
);

export const PendingChangeBanner = ({ p, isAdmin, own, onDone }) => {
  const pend = p.pending_change;
  const [busy, setBusy] = useState(false);
  if (!pend) return null;
  const mine = pend.proposed_by_agency === own;
  const c = pend.changes;
  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/partnerships/${p.id}/contract/${action}`);
      toast.success({ accept: 'Nouveau contrat appliqué', reject: 'Modification refusée', cancel: 'Proposition annulée' }[action]);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3 space-y-1" data-testid={`partnership-pending-change-${p.id}`}>
      <p className="text-xs font-bold text-amber-800">
        {mine ? 'Votre proposition de modification attend la réponse du partenaire' : `${pend.proposed_agency_name} propose de modifier le contrat`}
      </p>
      <Diff label="Commission" before={`${p.commission_split}%`} after={`${c.commission_split}%`} />
      <Diff label="Conditions" before={p.terms} after={c.terms} />
      <Diff label="Début" before={fmt(p.start_date)} after={fmt(c.start_date)} />
      <Diff label="Fin" before={fmt(p.end_date)} after={fmt(c.end_date)} />
      {isAdmin && (
        <div className="flex gap-2 pt-1">
          {mine ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => act('cancel')} className="rounded-xl h-8" data-testid={`contract-cancel-${p.id}`}>Annuler ma proposition</Button>
          ) : (
            <>
              <Button size="sm" disabled={busy} onClick={() => act('accept')} className="rounded-xl h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`contract-accept-${p.id}`}><Check className="w-4 h-4 mr-1" /> Accepter</Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => act('reject')} className="rounded-xl h-8" data-testid={`contract-reject-${p.id}`}><X className="w-4 h-4 mr-1" /> Refuser</Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const CARD_FIELDS = [['name', "Nom de l'agence"], ['contact_name', 'Responsable'], ['city', 'Ville'], ['phone', 'Téléphone'], ['email', 'E-mail'], ['address', 'Adresse']];

export const ContractEditDialog = ({ p, onClose, onDone }) => {
  const [form, setForm] = useState({});
  const [card, setCard] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!p) return;
    setForm({ commission_split: p.commission_split, terms: p.terms || '', start_date: p.start_date || '', end_date: p.end_date || '' });
    setCard(Object.fromEntries(CARD_FIELDS.map(([k]) => [k, p.partner?.[k] || ''])));
  }, [p]);
  if (!p) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    const split = Number(form.commission_split);
    if (form.commission_split === '' || split < 0 || split > 100) { toast.error('Pourcentage invalide (0 à 100)'); return; }
    if (p.external && !card.name?.trim()) { toast.error("Le nom de l'agence est requis"); return; }
    setBusy(true);
    try {
      await api.put(`/partnerships/${p.id}/contract`, {
        commission_split: split, terms: form.terms || null, start_date: form.start_date || null, end_date: form.end_date || null,
        ...(p.external ? { partner: card } : {}),
      });
      toast.success(p.external ? 'Contrat mis à jour' : 'Proposition envoyée au partenaire');
      onClose(); onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="contract-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#2B3A2C] flex items-center gap-2"><FileSignature className="w-5 h-5 text-[#B85C38]" /> Contrat avec {p.partner?.name}</DialogTitle>
          <DialogDescription>
            {p.external ? 'Agence hors plateforme : les modifications sont appliquées immédiatement.' : "L'autre agence devra accepter ; l'ancien contrat reste en vigueur en attendant."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 text-xs font-semibold text-gray-600">Part de commission pour l'agence qui recommande (%)
            <input type="number" min="0" max="100" value={form.commission_split} onChange={set('commission_split')} className={inputCls} data-testid="contract-split-input" />
          </label>
          <label className="text-xs font-semibold text-gray-600">Début du contrat
            <input type="date" value={form.start_date} onChange={set('start_date')} className={inputCls} data-testid="contract-start-input" />
          </label>
          <label className="text-xs font-semibold text-gray-600">Fin du contrat
            <input type="date" value={form.end_date} onChange={set('end_date')} className={inputCls} data-testid="contract-end-input" />
          </label>
          <label className="col-span-2 text-xs font-semibold text-gray-600">Conditions du partenariat
            <textarea rows={4} maxLength={3000} value={form.terms} onChange={set('terms')} className={inputCls}
              placeholder="Ex : exclusivité sur une zone, délais de paiement des commissions, obligations de chaque agence…" data-testid="contract-terms-input" />
          </label>
          {p.external && CARD_FIELDS.map(([k, label]) => (
            <label key={k} className={`text-xs font-semibold text-gray-600 ${k === 'name' || k === 'address' ? 'col-span-2' : ''}`}>{label}
              <input value={card[k] || ''} onChange={(e) => setCard((c) => ({ ...c, [k]: e.target.value }))} className={inputCls} data-testid={`contract-partner-${k}-input`} />
            </label>
          ))}
        </div>
        <Button onClick={submit} disabled={busy} className="w-full bg-[#B85C38] hover:bg-[#a04d2f] text-white rounded-xl" data-testid="contract-submit">
          {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} {p.external ? 'Enregistrer' : 'Proposer la modification'}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
